/*
  hanger-look.js — enhancement layer for the Hanger-style skin.
  Original implementation for The Ark. Vanilla JS, no dependencies, ~4 KB.
  Handles: featured-tabs switching + carousel arrows, category carousel
  arrows, video hero autoplay/pause + ready fade, PDP buy-row grouping,
  and skeleton→loaded marking for media. Everything is idempotent (per-
  element guards) so it is safe in the Shopify theme editor and safe if
  the script is evaluated more than once.
*/
(function () {
  'use strict';

  /* ==== VERSION MARKER — open your browser Console (F12) on the live site.
     If you do NOT see this line, the OLD hanger-look.js is still live and the
     fullscreen arrows/dots CANNOT work until you replace assets/hanger-look.js. */
  

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Metered or very slow connections: keep the poster, never spend the data. */
  var SAVE_DATA = (function () {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    return !!c.saveData || /(^|-)2g$/.test(c.effectiveType || '');
  })();

  /* Safari only shipped requestIdleCallback in 17.4 — without the fallback the
     video-hero hydration chain never completes on older iOS. */
  function onIdle(fn) {
    if (window.requestIdleCallback) requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 200);
  }

  function afterLoad(fn) {
    if (document.readyState === 'complete') onIdle(fn);
    else window.addEventListener('load', function () { onIdle(fn); }, { once: true });
  }

  function scrollStep(track) {
    var item = track.querySelector(':scope > *');
    var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 16;
    return item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
  }

  function smoothScrollBy(track, left) {
    track.scrollBy({ left: left, behavior: REDUCED ? 'auto' : 'smooth' });
  }

  /* Coalesce anything that touches layout into one call per frame. */
  function rafOnce(fn) {
    var queued = false;
    return function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; fn(); });
    };
  }

  function updateArrowState(track, prev, next) {
    if (!track || !prev || !next) return;

    /* ALL READS FIRST. The old order read scrollWidth/clientWidth, WROTE
       prev.disabled (which invalidates style via :disabled), then READ
       scrollLeft again - a forced synchronous layout, and Lighthouse's single
       biggest first-party reflow at 50ms. */
    var scrollLeft = track.scrollLeft;
    var max = track.scrollWidth - track.clientWidth - 2;

    var atStart = scrollLeft <= 2;
    var atEnd = scrollLeft >= max;

    /* Then writes, and only on change - a no-op write still dirties style. */
    if (prev.disabled !== atStart) prev.disabled = atStart;
    if (next.disabled !== atEnd) next.disabled = atEnd;
  }

  /* Init-time calls must not force layout during script evaluation; inside rAF
     the layout is happening anyway, so the read is free. */
  function updateArrowStateSoon(track, prev, next) {
    requestAnimationFrame(function () { updateArrowState(track, prev, next); });
  }

  /* ------- Featured tabs ------- */
  function initFeaturedTabs(root) {
    if (root.dataset.hftReady) return;
    root.dataset.hftReady = 'true';

    var tabs = root.querySelectorAll('[data-hft-tab]');
    var panels = root.querySelectorAll('[data-hft-panel]');
    var prev = root.querySelector('[data-hft-prev]');
    var next = root.querySelector('[data-hft-next]');

    function activeTrack() {
      var panel = root.querySelector('[data-hft-panel].is-active');
      return panel ? panel.querySelector('[data-hft-track]') : null;
    }

    /* Panels 2..N ship as <template data-hft-deferred>. Move the fragment into
       the live DOM the first time its tab is tapped: those ~360 nodes, ~30
       custom-element upgrades and 10 ModalDialog body-appends all happen here
       instead of during the initial parse. */
    function hydrate(panel) {
      if (!panel) return null;
      var tpl = panel.querySelector('template[data-hft-deferred]');
      if (tpl) {
        panel.insertBefore(tpl.content, tpl);
        tpl.parentNode.removeChild(tpl);

        /* the crawlable stand-in has served its purpose; drop it so the same
           products are not linked twice */
        var seo = panel.querySelector('.hft__seo-links');
        if (seo && seo.parentNode) seo.parentNode.removeChild(seo);

        bindTrack(panel);
        /* mark the new media now rather than waiting 200ms for the observer */
        safe(function () { initMediaSkeleton(panel); });
      }
      return panel.querySelector('[data-hft-track]');
    }

    function bindTrack(p) {
      var track = p.querySelector('[data-hft-track]');
      if (!track || track.dataset.hftBound) return;
      track.dataset.hftBound = '1';
      if (!prev || !next) return;
      track.addEventListener('scroll', rafOnce(function () {
        if (p.classList.contains('is-active')) updateArrowState(track, prev, next);
      }), { passive: true });
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        if (tab.classList.contains('is-active')) return;
        var idx = tab.getAttribute('data-hft-tab');
        tabs.forEach(function (t) {
          var on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        panels.forEach(function (p) {
          var on = p.getAttribute('data-hft-panel') === idx;
          p.classList.toggle('is-active', on);
          if (on) {
            /* hydrate BEFORE unhiding so the browser lays the panel out once,
               and inside rAF so appending 10 modals to <body> does not land in
               the middle of the tap's event handler */
            requestAnimationFrame(function () {
              var track = hydrate(p);
              p.removeAttribute('hidden');
              if (track) {
                track.scrollLeft = 0;
                updateArrowStateSoon(track, prev, next);
              }
            });
          } else {
            p.setAttribute('hidden', '');
          }
        });
      });
    });

    if (prev && next) {
      prev.addEventListener('click', function () {
        var track = activeTrack();
        if (track) smoothScrollBy(track, -scrollStep(track));
      });
      next.addEventListener('click', function () {
        var track = activeTrack();
        if (track) smoothScrollBy(track, scrollStep(track));
      });
      panels.forEach(bindTrack);
      var t = activeTrack();
      if (t) updateArrowStateSoon(t, prev, next);
      window.addEventListener('resize', rafOnce(function () {
        var track = activeTrack();
        if (track) updateArrowState(track, prev, next);
      }));
    }
  }

  /* ------- Category carousel ------- */
  function initCategoryCarousel(root) {
    if (root.dataset.hccReady) return;
    root.dataset.hccReady = 'true';

    var track = root.querySelector('[data-hcc-track]');
    var prev = root.querySelector('[data-hcc-prev]');
    var next = root.querySelector('[data-hcc-next]');
    if (!track) return;

    if (prev && next) {
      prev.addEventListener('click', function () { smoothScrollBy(track, -scrollStep(track)); });
      next.addEventListener('click', function () { smoothScrollBy(track, scrollStep(track)); });
      track.addEventListener('scroll', rafOnce(function () { updateArrowState(track, prev, next); }), { passive: true });
      updateArrowStateSoon(track, prev, next);
    }
  }
  /* ------- Related products carousel arrows ------- */
  function initRelatedCarousel(root) {
    var track = root.querySelector('[data-hrp-track]');
    var prev = root.querySelector('[data-hrp-prev]');
    var next = root.querySelector('[data-hrp-next]');
    if (!track || !prev || !next) return;
    if (root.dataset.hrpReady) return;
    root.dataset.hrpReady = 'true';
    
    prev.addEventListener('click', function () { smoothScrollBy(track, -scrollStep(track)); });
    next.addEventListener('click', function () { smoothScrollBy(track, scrollStep(track)); });
    track.addEventListener('scroll', rafOnce(function () { updateArrowState(track, prev, next); }), { passive: true });
    updateArrowStateSoon(track, prev, next);
    window.addEventListener('resize', rafOnce(function () { updateArrowState(track, prev, next); }));
  }

  /* ------- Video hero -------------------------------------------------------
     The POSTER <img> is the LCP element and paints straight from the initial
     HTML. No <video> exists in the document until after window load + idle,
     and only ONE is ever created (matching the same media query the <picture>
     used), so the browser can never speculatively pull a 12 MB 1080p master
     before LCP.

     Previously: both <video autoplay> elements were in the markup, the visible
     one was found with getComputedStyle() in a loop (a forced style resolution
     on the LCP element during script evaluation), the hidden one had
     preload='none' set far too late to matter, and the video sat at opacity:0
     under an opaque shimmer until `loadeddata` — which is why every filmstrip
     frame was black.
     ------------------------------------------------------------------------ */
  function initVideoHero(root) {
    if (root.dataset.hvhReady) return;
    root.dataset.hvhReady = 'true';

    var media = root.querySelector('.hvh__media');
    var poster = root.querySelector('.hvh__poster');
    var toggle = root.querySelector('[data-hvh-toggle]');
    var design = !!(window.Shopify && window.Shopify.designMode);

    /* 1. Retire the skeleton when the POSTER paints, never the video. */
    function posterReady() { root.classList.add('is-ready'); }
    if (!poster || (poster.complete && poster.naturalWidth > 0)) {
      posterReady();
    } else {
      poster.addEventListener('load', posterReady, { once: true });
      poster.addEventListener('error', posterReady, { once: true });
    }

    /* 2. Pick exactly ONE template, using the same query string the <picture>
          used — read off the DOM so the two can never drift. Deliberately not
          re-evaluated on resize: a phone in landscape is 823px, which crosses
          the 749px cut, and swapping would download the second video. */
    var mq = root.getAttribute('data-hvh-mq') || '(max-width: 749px)';
    var wantMobile = !!(window.matchMedia && window.matchMedia(mq).matches);
    var tpl =
      root.querySelector('template[data-hvh-video="' + (wantMobile ? 'mobile' : 'desktop') + '"]') ||
      root.querySelector('template[data-hvh-video]');

    if (!tpl || !media || !tpl.content.firstElementChild) {
      if (toggle) toggle.hidden = true; /* poster-only or placeholder hero */
      return;
    }

    var video = null;
    var wantsPlay = true;

    function syncIcon() {
      var paused = !video || video.paused;
      root.classList.toggle('is-paused', paused);
      /* aria-label is this button's only accessible name, and the markup hard-codes
         "Pause video". Track the action the button performs, not the state it was
         rendered in. Reuses the strings theme.liquid already localises. */
      if (toggle) {
        var strings = window.accessibilityStrings || {};
        toggle.setAttribute(
          'aria-label',
          paused ? (strings.playSlideshow || 'Play video') : (strings.pauseSlideshow || 'Pause video')
        );
      }
    }

    function play() {
      wantsPlay = true;
      if (!video) { hydrate(); return; }
      var p = video.play();
      /* iOS Low Power Mode rejects play() outright — fall back to the manual
         play button rather than leaving a pause icon over a stopped video. */
      if (p && p.catch) p.catch(syncIcon);
    }

    function pause() {
      wantsPlay = false;
      if (video) video.pause();
      root.classList.add('is-paused');
    }

    function hydrate() {
      if (video) return;
      video = tpl.content.firstElementChild.cloneNode(true);

      /* iOS needs these as PROPERTIES, not just attributes, or autoplay is
         refused even on a muted inline video. webkit-playsinline covers iOS
         versions predating the standard attribute. */
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      var reveal = function () { video.classList.add('is-in'); };
      video.addEventListener('loadeddata', reveal, { once: true });
      video.addEventListener('playing', reveal, { once: true });

      video.addEventListener('error', function () {
        if (video && video.parentNode) video.parentNode.removeChild(video);
        video = null;
        if (toggle) toggle.hidden = true; /* keep the poster; never a black box */
      }, { once: true });

      /* Dawn's pauseAllMedia() (global.js) pauses every <video> whenever ANY
         modal opens OR closes — including the quick-view on this very page —
         and never resumes. Track the element's real state so the icon stays
         honest, and resume on modalClosed unless the user paused deliberately. */
      video.addEventListener('pause', syncIcon);
      video.addEventListener('play', syncIcon);

      media.appendChild(video);
      if (wantsPlay) play();
    }

    document.addEventListener('modalClosed', function () {
      if (video && wantsPlay && video.paused) play();
    });

    /* 3. The toggle works BEFORE hydration too, so tapping play on a
          reduced-motion / Save-Data / Low-Power device loads the video on
          demand instead of leaving a dead button. */
    if (toggle) {
      toggle.addEventListener('click', function () {
        if (!video || video.paused) play();
        else pause();
      });
    }

    /* 4. When to hydrate. */
    if (design) { hydrate(); return; } /* merchants must see it immediately */

    if (REDUCED || SAVE_DATA) {
      wantsPlay = false;
      root.classList.add('is-paused');
      return;
    }

    var armed = false;
    function arm() {
      if (armed) return;
      armed = true;
      afterLoad(function () {
        if (document.visibilityState === 'hidden') {
          document.addEventListener('visibilitychange', function once() {
            if (document.visibilityState === 'hidden') return;
            document.removeEventListener('visibilitychange', once);
            hydrate();
          });
          return;
        }
        hydrate();
      });
    }

    /* Only once the hero is actually on screen: someone who lands and
       immediately scrolls past never pays for the video at all. */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        arm();
      }, { rootMargin: '200px' });
      io.observe(root);
    } else {
      arm();
    }
  }

  /* ------- PDP: group quantity + buy buttons onto one row ------- */
  function initPdpBuyRow(container) {
    if (container.dataset.hpdpReady) return;
    container.dataset.hpdpReady = 'true';

    /* main-product.liquid now emits .hpdp-buyrow server-side, which is what stops the
       ~60-90px collapse this function used to cause after first paint. Building a second
       one would nest the row inside itself and tag the wrapper with .hpdp-buywrap, so
       bail. Still needed for quick-view and any section fetched over the Section
       Rendering API, and for block orders where the two blocks are not adjacent. */
    if (container.querySelector(':scope > .hpdp-buyrow')) return;

    var pf = container.querySelector('product-form.product-form');
    if (!pf) return;

    var buyWrap = pf;
    while (buyWrap.parentElement && buyWrap.parentElement !== container) {
      buyWrap = buyWrap.parentElement;
    }
    if (buyWrap.parentElement !== container) return;

    var qty = container.querySelector(':scope > .product-form__quantity, :scope > .product-form__input--quantity');
    var row = document.createElement('div');
    row.className = 'hpdp-buyrow';
    container.insertBefore(row, buyWrap);
    if (qty) row.appendChild(qty);
    buyWrap.classList.add('hpdp-buywrap');
    row.appendChild(buyWrap);
  }

  /* ------- PDP: interactive dots for the mobile media slider (replaces 1/N) ------- */
  function initGalleryDots(scope) {
    (scope || document).querySelectorAll('media-gallery .product__media-list.slider').forEach(function (track) {
      if (track.dataset.arkDots) return;
      var slides = Array.prototype.slice.call(track.querySelectorAll('.product__media-item'));
      if (slides.length < 2) return;
      var buttons = track.parentElement.querySelector('.slider-buttons');
      if (!buttons) return;
      track.dataset.arkDots = 'true';

      var dots = document.createElement('div');
      dots.className = 'hpdp-dots';
      slides.forEach(function (slide, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'hpdp-dot' + (i === 0 ? ' is-active' : '');
        b.setAttribute('aria-label', 'Go to image ' + (i + 1));
        b.addEventListener('click', function () {
          var tr = track.getBoundingClientRect();
          var sr = slide.getBoundingClientRect();
          track.scrollBy({ left: (sr.left + sr.width / 2) - (tr.left + tr.width / 2), behavior: 'smooth' });
        });
        dots.appendChild(b);
      });
      buttons.appendChild(dots);

      var raf = null;
      function sync() {
        raf = null;
        var tr = track.getBoundingClientRect();
        var center = tr.left + tr.width / 2;
        var active = 0, best = Infinity;
        slides.forEach(function (slide, i) {
          var sr = slide.getBoundingClientRect();
          var d = Math.abs((sr.left + sr.width / 2) - center);
          if (d < best) { best = d; active = i; }
        });
        var ds = dots.children;
        for (var i = 0; i < ds.length; i++) ds[i].classList.toggle('is-active', i === active);
      }
      track.addEventListener('scroll', function () {
        if (!raf) raf = requestAnimationFrame(sync);
      }, { passive: true });
    });
  }

  /* ------- Lightbox: prev/next arrows + dots for the fullscreen image slider ------- */
  /* Armed once; fires the dot build after the dialog is actually open. */
  var pxArmed = false;
  function armLightboxDots() {
    if (pxArmed) return;
    pxArmed = true;
    document.addEventListener(
      'click',
      function (e) {
        if (!e.target || !e.target.closest) return;
        if (!e.target.closest('.product__modal-opener, .product__media-toggle')) return;
        /* ModalDialog.show() sets [open] during this same click; measure after it. */
        setTimeout(function () { safe(function () { initLightboxDots(document); }); }, 0);
      },
      true
    );
  }

  function initLightboxDots(scope) {
    var tracks = (scope || document).querySelectorAll('.product-media-modal__content');
    tracks.forEach(function (track) {
      if (track.dataset.arkPxDots) return;
      /* The lightbox is display:none until it opens (section-main-product.css), so
         every getBoundingClientRect() below would read 0 - and running this at init
         put 1 + N forced layouts (one per product media) on the critical path for a
         dialog nobody had opened. armLightboxDots() re-runs it on first open. */
      var pxModal = track.closest('.product-media-modal');
      if (pxModal && !pxModal.hasAttribute('open')) return;
      var slides = Array.prototype.slice.call(track.children).filter(function (c) {
        if (!c || c.nodeType !== 1) return false;
        /* Dawn renders each fullscreen image as a DIRECT <img> child of the modal
           content — accept the media element ITSELF, not only wrappers around one. */
        if (c.matches && c.matches('img, video, iframe, model-viewer, deferred-media, product-model')) return true;
        return !!(c.querySelector && c.querySelector('img, video, iframe, model-viewer'));
      });
      if (slides.length < 2) return;
      track.dataset.arkPxDots = 'true';
      slides.forEach(function (s) {
        s.setAttribute('tabindex', '-1');
        s.addEventListener('mousedown', function (e) { e.preventDefault(); });
      });
      
      var dialog = track.closest('.product-media-modal__dialog') || track.parentElement;

      function current() {
        var tr = track.getBoundingClientRect();
        var center = tr.left + tr.width / 2;
        var active = 0, best = Infinity;
        slides.forEach(function (slide, i) {
          var sr = slide.getBoundingClientRect();
          var d = Math.abs((sr.left + sr.width / 2) - center);
          if (d < best) { best = d; active = i; }
        });
        return active;
      }
      function goTo(i) {
        i = Math.max(0, Math.min(slides.length - 1, i));
        var slide = slides[i];
        var tr = track.getBoundingClientRect();
        var sr = slide.getBoundingClientRect();
        track.scrollBy({ left: (sr.left + sr.width / 2) - (tr.left + tr.width / 2), behavior: REDUCED ? 'auto' : 'smooth' });
      }
      

      /* prev / next arrows */
      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'hpx-arrow hpx-arrow--prev';
      prev.setAttribute('aria-label', 'Previous image');
      prev.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      

      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'hpx-arrow hpx-arrow--next';
      next.setAttribute('aria-label', 'Next image');
      next.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      

      /* dots */
      var dots = document.createElement('div');
      dots.className = 'hpx-dots';
      slides.forEach(function (slide, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'hpx-dot' + (i === 0 ? ' is-active' : '');
        b.setAttribute('aria-label', 'Image ' + (i + 1));
        dots.appendChild(b);
      });

      dialog.appendChild(prev);
      dialog.appendChild(next);
      dialog.appendChild(dots);
      var counter = document.createElement('div');
      counter.className = 'hpx-counter';
      counter.textContent = '1 / ' + slides.length;
      dialog.appendChild(counter);

      var modalRoot = track.closest('product-modal, .product-media-modal') || dialog;
      modalRoot.addEventListener('click', function (e) {
        var arrow = e.target.closest && e.target.closest('.hpx-arrow');
        var dot = e.target.closest && e.target.closest('.hpx-dot');
        if (!arrow && !dot) return;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (arrow) {
          goTo(current() + (arrow.classList.contains('hpx-arrow--next') ? 1 : -1));
        } else {
          var all = Array.prototype.slice.call(dots.children);
          goTo(all.indexOf(dot));
        }
      }, true);

      ['pointerdown', 'pointerup', 'mousedown', 'mouseup'].forEach(function (evt) {
        modalRoot.addEventListener(evt, function (e) {
          if (!e.target.closest) return;
          if (!e.target.closest('.hpx-arrow, .hpx-dot')) return;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }, true);
      });

      ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'click'].forEach(function (evt) {
        modalRoot.addEventListener(evt, function (e) {
          if (!e.target.closest) return;
          if (e.target.closest('.hpx-arrow, .hpx-dot, .product-media-modal__toggle')) return;
          if (!e.target.closest('.product-media-modal__content')) return;
          e.stopPropagation();
          e.stopImmediatePropagation();
        }, true);
      });

      

      var raf = null;
      function sync() {
        raf = null;
        var active = current();
        counter.textContent = (active + 1) + ' / ' + slides.length;
        var ds = dots.children;
        for (var i = 0; i < ds.length; i++) ds[i].classList.toggle('is-active', i === active);
        prev.classList.toggle('is-disabled', active <= 0);
        next.classList.toggle('is-disabled', active >= slides.length - 1);
      }
      track.addEventListener('scroll', function () {
        if (!raf) raf = requestAnimationFrame(sync);
      }, { passive: true });

      var settleTimer = null;
      track.addEventListener('scroll', function () {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(function () {
          var i = current();
          var slide = slides[i];
          var tr = track.getBoundingClientRect();
          var sr = slide.getBoundingClientRect();
          var off = (sr.left + sr.width / 2) - (tr.left + tr.width / 2);
          if (Math.abs(off) > 2) {
            track.scrollBy({ left: off, behavior: REDUCED ? 'auto' : 'smooth' });
          }
        }, 120);
      }, { passive: true });

      /* keyboard arrows while the lightbox is open */
      document.addEventListener('keydown', function (e) {
        var modalEl = track.closest('product-modal, .product-media-modal');
        if (!modalEl || !modalEl.hasAttribute('open')) return;
        if (e.key === 'ArrowLeft') { goTo(current() - 1); }
        else if (e.key === 'ArrowRight') { goTo(current() + 1); }
      });

      /* When the modal opens, land on the image that was clicked (Dawn's own
         scroll code targets its vertical layout and resets our slider), then
         keep the dots in sync. */
      function slideMediaId(el) {
        if (!el || !el.getAttribute) return null;
        var id = el.getAttribute('data-media-id');
        if (id) return id;
        var inner = el.querySelector && el.querySelector('[data-media-id]');
        return inner ? inner.getAttribute('data-media-id') : null;
      }
      var modal = track.closest('product-modal, .product-media-modal');
      if (modal && 'MutationObserver' in window) {
        new MutationObserver(function () {
          if (!modal.hasAttribute('open')) return;
          setTimeout(function () {
            var opener = modal.openedBy;
            if (opener && opener.getAttribute) {
              var mid = opener.getAttribute('data-media-id');
              if (mid) {
                for (var i = 0; i < slides.length; i++) {
                  if (slideMediaId(slides[i]) === String(mid)) { goTo(i); break; }
                }
              }
            }
            sync();
          }, 80);
          setTimeout(sync, 380);
        }).observe(modal, { attributes: true, attributeFilter: ['open'] });
      }
      sync();
    });
  }

  /* ------- Skeletons: mark media hosts loaded, fade late images ------- */
  var HOST_SELECTOR = '.hpc__media, .hcc__media, .card__media .media, .product__media.media, .slideshow .banner__media';

  function markLoaded(host) {
    if (!host.classList.contains('h-loaded')) host.classList.add('h-loaded');
  }

  function initMediaSkeleton(scope) {
    var hosts = (scope || document).querySelectorAll(HOST_SELECTOR);
    hosts.forEach(function (host) {
      if (host.dataset.hSkel) return;
      host.dataset.hSkel = 'true';
      var imgs = host.querySelectorAll('img');
      if (!imgs.length) {
        /* placeholder SVG or empty — no network wait */
        markLoaded(host);
        return;
      }
      var first = imgs[0];
      if (first.complete && first.naturalWidth > 0) {
        markLoaded(host);
      } else {
        var done = function () {
          if (!REDUCED) first.classList.add('h-img-in');
          markLoaded(host);
        };
        first.addEventListener('load', done, { once: true });
        first.addEventListener('error', function () { markLoaded(host); }, { once: true });
      }
    });
  }

  /* ------- PDP: floating add-to-cart bar (appears when buy box scrolls away) ------- */
  function initStickyAtc() {
    if (document.querySelector('.hatc')) return;
    var info = document.querySelector('.product__info-container');
    if (!info || !('IntersectionObserver' in window)) return;
    var submit = info.querySelector('.product-form__submit');
    var watch = info.querySelector('.hpdp-buyrow') || info.querySelector('product-form.product-form');
    if (!submit || !watch) return;

    var titleEl = document.querySelector('.product__title h1');
    var priceEl = info.querySelector('.price-item--regular, .price-item');
    var img = document.querySelector('.product__media img');

    var bar = document.createElement('div');
    bar.className = 'hatc';
    bar.setAttribute('aria-hidden', 'true');

    if (img) {
      var thumb = document.createElement('img');
      thumb.className = 'hatc__thumb';
      thumb.src = img.currentSrc || img.src;
      thumb.alt = '';
      thumb.loading = 'lazy';
      bar.appendChild(thumb);
    }
    var meta = document.createElement('div');
    meta.className = 'hatc__meta';
    var t = document.createElement('span');
    t.className = 'hatc__title';
    t.textContent = titleEl ? titleEl.textContent.trim() : '';
    var v = document.createElement('span');
    v.className = 'hatc__variant';
    var p = document.createElement('span');
    p.className = 'hatc__price';
    p.textContent = priceEl ? priceEl.textContent.trim() : '';
    meta.appendChild(t);
    meta.appendChild(v);
    meta.appendChild(p);
    bar.appendChild(meta);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hatc__btn';
    btn.textContent = submit.textContent.trim() || 'Add to cart';
    btn.addEventListener('click', function () {
      if (submit.getAttribute('disabled') === null && submit.getAttribute('aria-disabled') !== 'true') {
        submit.click();
      } else {
        info.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'center' });
      }
    });
    bar.appendChild(btn);
    document.body.appendChild(bar);

    function updateVariant() {
      var picked = [];
      info.querySelectorAll('input[type="radio"]:checked').forEach(function (i) { picked.push(i.value); });
      v.textContent = picked.join(' / ');
      var priceNow = info.querySelector('.price-item--regular, .price-item');
      if (priceNow) p.textContent = priceNow.textContent.trim();
      var soldOut = submit.getAttribute('disabled') !== null || submit.getAttribute('aria-disabled') === 'true';
      btn.classList.toggle('is-disabled', soldOut);
      btn.textContent = soldOut ? submit.textContent.trim() : (submit.textContent.trim() || 'Add to cart');
    }
    info.addEventListener('change', function () { setTimeout(updateVariant, 100); });
    updateVariant();

    var io = new IntersectionObserver(function (entries) {
      var show = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    });
    io.observe(watch);
  }

  /* ------- Footer: collapsible blocks on phone (Hanger-style accordion) ------- */
  function initFooterAccordion(scope) {
    /* The control used to be role="button" + tabindex on the <h2> itself. An h2
       has no compatible ARIA role, so that failed "Uses ARIA roles only on
       compatible elements" AND the Agentic Browsing accessibility-tree audit.
       It was also built at every viewport while toggle() bailed above 749px,
       leaving desktop with a focusable control that did nothing.

       Now: a real <button> wrapping the heading text, created only while the
       phone media query matches, torn down again above it. A native button
       brings Enter/Space, the correct role and focus handling for free. */
    var mq = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;

    (scope || document).querySelectorAll('.footer .footer-block').forEach(function (block) {
      if (block.dataset.arkAcc) return;
      var heading = block.querySelector('.footer-block__heading');
      if (!heading || !heading.nextElementSibling) return;
      block.dataset.arkAcc = 'true';
      block.classList.add('hfoot-acc');

      var btn = null;

      function toggle() {
        var open = block.classList.toggle('is-open');
        if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      function build() {
        if (btn) return;
        btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'footer-block__heading-btn';
        btn.setAttribute('aria-expanded', block.classList.contains('is-open') ? 'true' : 'false');
        while (heading.firstChild) btn.appendChild(heading.firstChild);
        heading.appendChild(btn);
        btn.addEventListener('click', toggle);
      }

      function teardown() {
        if (!btn) return;
        btn.removeEventListener('click', toggle);
        while (btn.firstChild) heading.insertBefore(btn.firstChild, btn);
        heading.removeChild(btn);
        btn = null;
        /* desktop shows every column open; drop the collapsed state with it */
        block.classList.remove('is-open');
      }

      function sync() {
        if (!mq || mq.matches) build();
        else teardown();
      }

      sync();
      if (mq) {
        if (mq.addEventListener) mq.addEventListener('change', sync);
        else if (mq.addListener) mq.addListener(sync);
      }
    });
  }

  /* Run each initialiser in isolation so one throwing can NEVER stop the others
     (e.g. a gallery quirk must not prevent the fullscreen arrows/dots binding). */
  function safe(fn) {
    try { fn(); } catch (e) { if (window.console && console.warn) console.warn('[ark] init skipped:', e); }
  }

  function initAll(scope) {
    var root = scope || document;

    /* This runs SYNCHRONOUSLY during deferred-script evaluation (readyState is
       already 'interactive'), so every traversal here is on the critical path.
       Six of the eleven matched nothing on non-product templates. One cheap
       probe decides whether the product-detail initialisers can match at all.

       Deliberately NOT keyed on main[data-template]: the hanger sections can be
       dropped on any template, and featured-product.liquid puts
       .product__info-container on non-product pages. */
    var hasProduct = !!(root.querySelector && root.querySelector('product-info, .product__info-container'));

    safe(function () { root.querySelectorAll('[data-hft]').forEach(initFeaturedTabs); });
    safe(function () { root.querySelectorAll('[data-hcc]').forEach(initCategoryCarousel); });
    safe(function () { root.querySelectorAll('[data-hvh]').forEach(initVideoHero); });
    safe(function () { initMediaSkeleton(root); });
    safe(function () { initFooterAccordion(root); });

    if (!hasProduct) return;

    /* Popups and recommendation carousels can arrive on any template, but only
       ever AFTER load (quick view, related-products fetch) - MutationObserver #1
       covers that. At init they exist only on product pages. */
    safe(function () { root.querySelectorAll('.product__info-container').forEach(initPdpBuyRow); });
    safe(function () { initGalleryDots(root); });
    safe(function () { initLightboxDots(root); });   /* no-ops until the modal is open */
    safe(function () { armLightboxDots(); });
    safe(function () { initStickyAtc(); });
    safe(function () { portalPopups(); });
    safe(function () { root.querySelectorAll('product-recommendations').forEach(initRelatedCarousel); });
  }

  /* Size-chart (and any product popup) rendered inside the product section can
     be trapped by transformed ancestors — position:fixed then anchors to the
     ancestor, not the viewport, and the popup shows sandwiched inside the page.
     Moving it to <body> makes the overlay bulletproof; Dawn's modal-opener
     looks the dialog up globally by id, so behaviour is unchanged. */
  function portalPopups() {
    document.querySelectorAll('modal-dialog.product-popup-modal, .product-popup-modal').forEach(function (m) {
      if (m.dataset.arkPortal || m.parentElement === document.body) return;
      m.dataset.arkPortal = 'true';
      document.body.appendChild(m);
    });
  }

  /* ZERO-TIMING GUARANTEE: also portal at the moment the opener is tapped, in
     the CAPTURE phase (runs before Dawn's own click handler shows the dialog).
     Covers dialogs injected at any time by any flow — page, quick view, apps. */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var opener = e.target.closest('modal-opener[data-modal], [data-modal*="PopupModal"], a[href^="#PopupModal"]');
    if (!opener) return;
    var sel = opener.getAttribute('data-modal') || opener.getAttribute('href');
    if (!sel || sel.indexOf('PopupModal') === -1) return;
    var m = null;
    try { m = document.querySelector(sel); } catch (err) { m = null; }
    if (m && m.parentElement !== document.body) {
      m.dataset.arkPortal = 'true';
      document.body.appendChild(m);
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Late-rendered content (product recommendations swap, predictive search,
     infinite content from apps): re-mark new media, debounced. */
  /* PERF: observing document.body {childList, subtree} meant every <script>,
     <iframe>, <link> and tracking <div> injected by GTM / Meta / Clarity /
     Shopify's web-pixels-manager triggered four full-document sweeps. None of
     those nodes can contain a media host, a lightbox or a popup, so filter the
     records before scheduling any work. */
  var MO_IGNORE = { SCRIPT: 1, IFRAME: 1, LINK: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1, META: 1 };
  var MO_WATCH = HOST_SELECTOR +
    ', .product-media-modal__content, modal-dialog.product-popup-modal, .product-popup-modal, product-recommendations';

  function moRelevant(node) {
    if (!node || node.nodeType !== 1) return false;   /* text / comment */
    if (MO_IGNORE[node.tagName] === 1) return false;  /* third-party tags */
    if (!node.matches) return false;
    return node.matches(MO_WATCH) || !!node.querySelector(MO_WATCH);
  }

  var moTimer = null;
  var mo = new MutationObserver(function (records) {
    if (moTimer) return;

    var hit = false;
    for (var i = 0; i < records.length && !hit; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (moRelevant(added[j])) { hit = true; break; }
      }
    }
    if (!hit) return;

    moTimer = setTimeout(function () {
      moTimer = null;
      safe(function () { initMediaSkeleton(document); });
      /* the fullscreen media modal can be (re)rendered late — re-bind its
         arrows + dots too (guarded, so it's a no-op if already bound). */
      safe(function () { initLightboxDots(document); });
      /* quick-view content is INJECTED after load and can carry a size-chart
         dialog — teleport it to <body> too, or the sheet's transform cages it */
      safe(function () { portalPopups(); });
      safe(function () { document.querySelectorAll('product-recommendations').forEach(initRelatedCarousel); });
    }, 200);
  });
  if (document.body) {
    mo.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      mo.observe(document.body, { childList: true, subtree: true });
    });
  }

  /* Theme editor support */
  document.addEventListener('shopify:section:load', function (evt) {
    initAll(evt.target);
  });
})();
/* ============================================================================
   MEGA MENU — single consolidated controller (v40)
   - hover opens temporarily; click pins it open (click again / X / outside /
     Escape closes)
   - only ONE menu open at a time (opening any menu closes the others)
   - hovering a category swaps the right-hand image instantly; the first
     category is shown by default and restored when the cursor leaves the list
   Guarded so it binds each menu once, and re-binds after theme-editor reloads.
   ============================================================================ */
(function () {
  function closeMenuEl(m) {
    m.classList.remove('is-pinned', 'is-opening');
    m.removeAttribute('open');
  }
  function closeOthers(except) {
    document.querySelectorAll('details.mega-menu[open]').forEach(function (m) {
      if (m !== except) closeMenuEl(m);
    });
  }

  function initMega(root) {
    (root || document).querySelectorAll('details.mega-menu').forEach(function (menu) {
      if (menu.dataset.arkMega) return;
      menu.dataset.arkMega = '1';

      var leaveTimeout;
      var summary = menu.querySelector('summary');
      if (!summary) return;
      var closeBtn = menu.querySelector('.mega-menu__close');
      var imgs = menu.querySelectorAll('.mega-menu__featured-img');
      var area = menu.querySelector('.mega-menu__links-area');

      function showDefaultImage() {
        imgs.forEach(function (img, i) { img.classList.toggle('is-active', i === 0); });
      }

      // Hovering a category → activate the image whose key matches; default first.
      menu.querySelectorAll('.mega-menu__sublink[data-mega-img]').forEach(function (a) {
        a.addEventListener('mouseenter', function () {
          var key = a.getAttribute('data-mega-img');
          imgs.forEach(function (img) {
            img.classList.toggle('is-active', img.getAttribute('data-mega-key') === key);
          });
        });
      });
      if (area) area.addEventListener('mouseleave', showDefaultImage);

      function openMenu() {
        closeOthers(menu);           // enforce single-open
        menu.classList.remove('is-opening');
        void menu.offsetHeight;       // reflow so the reveal animation re-fires
        menu.setAttribute('open', '');
        menu.classList.add('is-opening');
        showDefaultImage();
      }

      // Hover: open temporarily.
      menu.addEventListener('mouseenter', function () {
        clearTimeout(leaveTimeout);
        if (!menu.hasAttribute('open')) openMenu();
      });
      // Leave: close after a short grace period — unless it was pinned by a click.
      menu.addEventListener('mouseleave', function () {
        if (menu.classList.contains('is-pinned')) return;
        leaveTimeout = setTimeout(function () { closeMenuEl(menu); }, 180);
      });
      // Click: toggle pinned (stays open until closed explicitly).
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (menu.classList.contains('is-pinned')) {
          closeMenuEl(menu);
        } else {
          closeOthers(menu);
          if (!menu.hasAttribute('open')) openMenu();
          menu.classList.add('is-pinned');
        }
      });

      menu._close = function () { closeMenuEl(menu); };
      if (closeBtn) {
        closeBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          closeMenuEl(menu);
          summary.focus();
        });
      }
    });
  }

  if (!window.__arkMegaDoc) {
    window.__arkMegaDoc = true;
    // Click anywhere outside an open menu closes it.
    document.addEventListener('click', function (e) {
      document.querySelectorAll('details.mega-menu[open]').forEach(function (menu) {
        if (!menu.contains(e.target)) closeMenuEl(menu);
      });
    });
    // Escape closes any open menu.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('details.mega-menu[open]').forEach(function (menu) {
        var s = menu.querySelector('summary');
        closeMenuEl(menu);
        if (s) s.focus();
      });
    });
    document.addEventListener('shopify:section:load', function () { initMega(document); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initMega(document); });
  } else {
    initMega(document);
  }
})();

/* ============================================================================
   QUICK-VIEW GALLERY DOTS
   The quick-view modal content is injected via innerHTML, so inline <script>
   tags inside the section never run. A single delegated listener on document
   handles the dots for whatever quick-view is currently open. Clicking a dot
   activates the matching slide (number of dots === number of images).
   ============================================================================ */
(function () {
  if (window.__arkHqvDots) return;
  window.__arkHqvDots = true;

  function activate(mediaCol, idx) {
    mediaCol.querySelectorAll('[data-hqv-slide]').forEach(function (slide) {
      slide.classList.toggle('is-active', slide.getAttribute('data-hqv-slide') === idx);
    });
    mediaCol.querySelectorAll('[data-hqv-dot]').forEach(function (dot) {
      dot.classList.toggle('is-active', dot.getAttribute('data-hqv-dot') === idx);
    });
  }

  document.addEventListener('click', function (e) {
    var dot = e.target.closest ? e.target.closest('[data-hqv-dot]') : null;
    if (!dot) return;
    var mediaCol = dot.closest('.hqv__media-col');
    if (!mediaCol) return;
    e.preventDefault();
    activate(mediaCol, dot.getAttribute('data-hqv-dot'));
  });

  /* --- swipe left/right on the image to change slides (phones) --- */
  function currentIndex(mediaCol) {
    var active = mediaCol.querySelector('[data-hqv-slide].is-active');
    return active ? parseInt(active.getAttribute('data-hqv-slide'), 10) : 0;
  }
  function slideCount(mediaCol) {
    return mediaCol.querySelectorAll('[data-hqv-slide]').length;
  }
  var swipeCol = null, swipeX = 0, swipeY = 0;
  document.addEventListener('touchstart', function (e) {
    if (e.touches.length !== 1 || !e.target.closest) return;
    var col = e.target.closest('.hqv__media-col');
    if (!col || slideCount(col) < 2) { swipeCol = null; return; }
    swipeCol = col;
    swipeX = e.touches[0].clientX;
    swipeY = e.touches[0].clientY;
  }, { passive: true });
  document.addEventListener('touchend', function (e) {
    if (!swipeCol || !e.changedTouches || !e.changedTouches.length) return;
    var col = swipeCol; swipeCol = null;
    var dx = e.changedTouches[0].clientX - swipeX;
    var dy = e.changedTouches[0].clientY - swipeY;
    /* horizontal swipe only — vertical stays free for the sheet drag-to-close */
    if (Math.abs(dx) < 42 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
    var n = slideCount(col);
    var next = currentIndex(col) + (dx < 0 ? 1 : -1);
    if (next < 0) next = 0;
    if (next > n - 1) next = n - 1;
    activate(col, String(next));
  }, { passive: true });
})();

/* ============================================================================
   QUICK-VIEW ADD-TO-CART GUARD
   The Add-to-cart button must reflect the SELECTED size's availability — not
   get stuck disabled. Dawn disables the button on every option change (loading
   state) and only re-enables it if its async re-render succeeds; if that fetch
   is unreliable inside the modal, the button stays disabled forever. This
   delegated guard resolves the selected variant from embedded data and sets the
   correct button/hidden-input state directly, so it never sticks. It runs after
   Dawn's synchronous disable (setTimeout 0) and agrees with Dawn on success.
   ============================================================================ */
(function () {
  if (window.__arkHqvAtc) return;
  window.__arkHqvAtc = true;

  function selectedValues(vs) {
    var out = [];
    vs.querySelectorAll('fieldset').forEach(function (fs) {
      var chk = fs.querySelector('input:checked');
      if (!chk) {
        var sel = fs.querySelector('select');
        if (sel && sel.selectedIndex > -1) chk = sel.options[sel.selectedIndex];
      }
      if (chk) out.push(chk.value);
    });
    return out;
  }

  function findVariant(variants, sel) {
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i].options || [];
      if (opts.length !== sel.length) continue;
      var ok = true;
      for (var j = 0; j < opts.length; j++) {
        if (String(opts[j]) !== String(sel[j])) { ok = false; break; }
      }
      if (ok) return variants[i];
    }
    return null;
  }

  function sync(root) {
    var dataEl = root.querySelector('[data-hqv-variants]');
    var vs = root.querySelector('variant-selects');
    if (!dataEl || !vs) return;
    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }

    var sel = selectedValues(vs);
    if (!sel.length) return;
    var match = findVariant(variants, sel);

    var btn = root.querySelector('[id^="ProductSubmitButton-"]');
    var idInput = root.querySelector('form input[name="id"]') || root.querySelector('input.product-variant-id');

    if (idInput) {
      idInput.value = match ? match.id : '';
      if (match) idInput.removeAttribute('disabled');
      else idInput.setAttribute('disabled', 'disabled');
    }
    if (!btn) return;
    var span = btn.querySelector('span');
    if (match && match.available) {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('disabled');
      if (span) span.textContent = btn.getAttribute('data-atc-label') || 'Add to cart';
    } else {
      btn.setAttribute('disabled', 'disabled');
      if (span) span.textContent = match ? 'Sold out' : 'Unavailable';
    }
  }

  document.addEventListener('change', function (e) {
    if (!e.target.closest) return;
    var root = e.target.closest('[data-quick-view="true"]');
    if (!root) return;
    // Run after Dawn's synchronous button-disable so our state wins.
    setTimeout(function () { sync(root); }, 0);
  });
})();

/* ============================================================================
   MOBILE MENU — drag-to-close + tap-outside (phone only)
   The drawer (fixed sheet below the header) can be dragged down to dismiss, and
   tapping outside the sheet closes it. Closing is done by clicking the drawer's
   own <summary> so Dawn's close animation/cleanup runs normally.
   ============================================================================ */
(function () {
  if (window.__arkMenuDrag) return;
  window.__arkMenuDrag = true;

  function isPhone() { return window.matchMedia('(max-width: 989px)').matches; }
  function detailsEl() { return document.getElementById('Details-menu-drawer-container'); }
  function menuOpen() { var d = detailsEl(); return !!(d && d.hasAttribute('open')); }
  function closeMenu() {
    var s = document.querySelector('#Details-menu-drawer-container > summary');
    if (s) s.click();
  }

  /* the filter sheet uses the same geometry, so it drags the same way */
  function facetsOpen() {
    var d = document.querySelector('.mobile-facets__disclosure[open]');
    return !!d;
  }
  function closeFacets() {
    var s = document.querySelector('.mobile-facets__wrapper > details > summary');
    if (s) s.click();
  }
  function cartOpen() {
    var d = document.querySelector('cart-drawer.active, .drawer.active');
    return !!d;
  }
  function closeCart() {
    var b = document.querySelector('cart-drawer .drawer__close');
    if (b) b.click();
  }

  /* Tap outside the sheet (while open) closes it. */
  document.addEventListener('click', function (e) {
    if (!isPhone() || !menuOpen() || !e.target.closest) return;
    if (e.target.closest('#menu-drawer')) return;                          // inside the sheet
    if (e.target.closest('#Details-menu-drawer-container > summary')) return; // the toggle itself
    closeMenu();
  });

  /* Drag the sheet down to dismiss — works with BOTH touch and mouse
     (so it also works in a desktop responsive/preview window). */
  var drawer = null, hd = null, scroller = null, startY = 0, dy = 0, dragging = false, mouse = false;

  function pointY(e) { return e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY; }

  var isFacets = false;
  var isCart = false;

  function startMenu(e, isMouse) {
    if (!isPhone() || drawer || !e.target.closest) return;

    var d = null, sc = null;
    if (menuOpen()) {
      d = e.target.closest('#menu-drawer');
      if (d) sc = d.querySelector('.menu-drawer__navigation-container');
    }
    if (!d && facetsOpen()) {
      d = e.target.closest('.mobile-facets__inner');
      if (d) { sc = d.querySelector('.mobile-facets__main'); isFacets = true; }
    }
    if (!d && cartOpen()) {
      d = e.target.closest('.cart-drawer .drawer__inner');
      if (d) { sc = d.querySelector('cart-drawer-items'); isCart = true; }
    }
    if (!d) return;
    if (!isFacets) isFacets = false;

    if (sc && sc.scrollTop > 2) return;
    drawer = d; scroller = sc; hd = d.closest('header-drawer') || d.closest('.mobile-facets__wrapper') || d.closest('cart-drawer'); mouse = isMouse;
    startY = pointY(e); dy = 0; dragging = false;
  }

  function moveMenu(e) {
    if (!drawer) return;
    var delta = pointY(e) - startY;
    if (delta <= 0) {
      if (dragging) { dragging = false; if (hd) hd.classList.remove('is-dragging'); drawer.style.removeProperty('--ark-drag'); }
      return;
    }
    if (scroller && scroller.scrollTop > 2) {
      if (dragging) { dragging = false; if (hd) hd.classList.remove('is-dragging'); drawer.style.removeProperty('--ark-drag'); }
      drawer = null; return;
    }
    dy = delta;
    if (!dragging && delta > 4) { dragging = true; if (hd) hd.classList.add('is-dragging'); }
    if (dragging) {
      /* The sheet's !important transform reads --ark-drag, so setting the
         variable inline is what actually moves it (style.transform cannot). */
      drawer.style.setProperty('--ark-drag', dy + 'px');
      if (!mouse && e.cancelable) e.preventDefault();
    }
  }

  function endMenu() {
    if (!drawer) return;
    var d = drawer, h = hd, moved = dy, was = dragging;
    if (h) h.classList.remove('is-dragging');   /* easing transition comes back */
    drawer = null; scroller = null; hd = null; dy = 0; dragging = false;
      if (was && moved > 80) {
      d.style.setProperty('--ark-drag', '110%');
      var closer = isCart ? closeCart : (isFacets ? closeFacets : closeMenu);
      isFacets = false;
      isCart = false;
      setTimeout(function () {
        closer();
        setTimeout(function () { d.style.removeProperty('--ark-drag'); }, 80);
      }, 260);
    } else {
      d.style.removeProperty('--ark-drag');
      isFacets = false;
      isCart = false;
    }
  }

  document.addEventListener('touchstart', function (e) { if (e.touches.length === 1) startMenu(e, false); }, { passive: true });
  document.addEventListener('touchmove', moveMenu, { passive: false });
  document.addEventListener('touchend', endMenu);
  document.addEventListener('touchcancel', endMenu);
  document.addEventListener('mousedown', function (e) { if (e.button === 0) startMenu(e, true); }, { passive: true });
  document.addEventListener('mousemove', moveMenu, { passive: true });
  document.addEventListener('mouseup', endMenu);

  /* Back-forward cache restores the page WITH the drawer's tapped state (open
     submenus, focused link) — reset it so nothing looks "selected" on return. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    try {
      var d = detailsEl();
      if (d) { d.classList.remove('menu-opening'); d.removeAttribute('open'); }
      document.querySelectorAll('#menu-drawer details[open]').forEach(function (dd) {
        dd.classList.remove('menu-opening');
        dd.removeAttribute('open');
      });
      document.querySelectorAll('#menu-drawer .submenu-open').forEach(function (el) {
        el.classList.remove('submenu-open');
      });
      document.body.classList.remove('overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden');
      if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    } catch (err) { /* never break the page over cleanup */ }
  });
})();
/* ---- price range slider (syncs with Dawn's two text inputs) ---- */
(function () {
  if (window.__arkPriceSlider) return;
  window.__arkPriceSlider = true;

  function texts(el) {
    var pr = el.closest('price-range');
    return pr ? pr.querySelectorAll('input[type="text"]') : null;
  }

  function paint(el) {
    var lo = el.querySelector('.ark-slider__input--min');
    var hi = el.querySelector('.ark-slider__input--max');
    var fill = el.querySelector('.ark-slider__fill');
    if (!lo || !hi || !fill) return;
    var max = parseFloat(el.dataset.max) || 1;
    fill.style.left = (parseFloat(lo.value) / max * 100) + '%';
    fill.style.right = (100 - parseFloat(hi.value) / max * 100) + '%';
  }

  function pull(el, source) {
    var lo = el.querySelector('.ark-slider__input--min');
    var hi = el.querySelector('.ark-slider__input--max');
    if (parseFloat(lo.value) > parseFloat(hi.value)) {
      if (source === lo) lo.value = hi.value; else hi.value = lo.value;
    }
    paint(el);
    var t = texts(el);
    if (!t || t.length < 2) return;
    var max = parseFloat(el.dataset.max) || 0;
    t[0].value = parseFloat(lo.value) > 0 ? lo.value : '';
    t[1].value = parseFloat(hi.value) < max ? hi.value : '';
  }

  class ArkPriceSlider extends HTMLElement {
    connectedCallback() { paint(this); }
  }
  customElements.define('ark-price-slider', ArkPriceSlider);

  document.addEventListener('input', function (e) {
    if (!e.target.closest) return;
    var el = e.target.closest('ark-price-slider');
    if (el && e.target.classList.contains('ark-slider__input')) pull(el, e.target);
  });

  document.addEventListener('change', function (e) {
    if (!e.target.closest) return;
    var el = e.target.closest('ark-price-slider');
    if (el && e.target.classList.contains('ark-slider__input')) {
      var t = texts(el);
      if (t && t.length) t[0].dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }
    var pr = e.target.closest('price-range');
    if (pr && e.target.type === 'text') {
      var s = pr.querySelector('ark-price-slider');
      if (!s) return;
      var tt = pr.querySelectorAll('input[type="text"]');
      s.querySelector('.ark-slider__input--min').value = parseFloat(tt[0].value.replace(/[^0-9.]/g, '')) || 0;
      s.querySelector('.ark-slider__input--max').value = parseFloat(tt[1].value.replace(/[^0-9.]/g, '')) || s.dataset.max;
      paint(s);
    }
  });
})();

/* ============================================================================
   QUICK VIEW — drag-to-close (phone only)
   Drag the bottom sheet down past a threshold to dismiss it.
   ============================================================================ */
(function () {
  if (window.__arkQvDrag) return;
  window.__arkQvDrag = true;

  function isPhone() { return window.matchMedia('(max-width: 749px)').matches; }
  var sheet = null, modal = null, scroller = null, startY = 0, dy = 0, dragging = false;

  document.addEventListener('touchstart', function (e) {
    if (!isPhone() || e.touches.length !== 1 || !e.target.closest) return;
    var m = e.target.closest('quick-add-modal[open]');
    if (!m) return;
    var c = m.querySelector('.quick-add-modal__content');
    if (!c || !c.contains(e.target)) return;
    if (c.scrollTop > 2) return;
    sheet = c; modal = m; scroller = c;
    startY = e.touches[0].clientY; dy = 0; dragging = false;
  }, { passive: true });

  document.addEventListener('touchmove', function (e) {
    if (!sheet) return;
    var delta = e.touches[0].clientY - startY;
    if (delta <= 0) {
      if (dragging) { dragging = false; modal.classList.remove('is-dragging'); sheet.style.transform = ''; }
      return;
    }
    if (scroller && scroller.scrollTop > 2) {
      if (dragging) { dragging = false; modal.classList.remove('is-dragging'); sheet.style.transform = ''; }
      sheet = null; return;
    }
    dy = delta; dragging = true;
    modal.classList.add('is-dragging');
    sheet.style.transform = 'translateY(' + dy + 'px)';
    if (e.cancelable) e.preventDefault();
  }, { passive: false });

  function endQv() {
    if (!sheet) return;
    var s = sheet, m = modal, moved = dy, was = dragging;
    m.classList.remove('is-dragging');
    sheet = null; modal = null; scroller = null; dy = 0; dragging = false;
    if (was && moved > 90) {
      s.style.transition = 'transform 0.28s ease';
      s.style.transform = 'translateY(100%)';
      setTimeout(function () {
        if (typeof m.hide === 'function') m.hide();
        else { var t = m.querySelector('.quick-add-modal__toggle'); if (t) t.click(); }
        s.style.transition = ''; s.style.transform = '';
      }, 240);
    } else {
      s.style.transform = '';
    }
  }
  document.addEventListener('touchend', endQv);
  document.addEventListener('touchcancel', endQv);
})();


/* ============================================================================
   FILTER SHEET — flat, no drill-down. Dawn's menu-drawer JS treats each filter
   summary as a submenu opener (hides the list, slides a panel in). The panel is
   already inline via CSS, so intercept the click in the capture phase and just
   toggle the <details> natively.

   PERF: this block is dead weight on every template that has no facets — index,
   product, page, article, cart. It used to observe document.documentElement with
   {childList, subtree}, the widest scope the API allows, and run TWO
   full-document querySelectorAll per mutation burst (one synchronously, one on a
   120ms re-arm) on pages with zero facets. Every script and iframe that GTM,
   Meta, Clarity and Shopify's web-pixels-manager inject paid that toll.
   ============================================================================ */
(function () {
  if (window.__arkFlatFacets) return;
  window.__arkFlatFacets = true;

  var FACET_SELECTOR = '.mobile-facets__details, .facets__disclosure-vertical';

  /* Facets are rendered server-side by snippets/facets.liquid, and assets/facets.js
     only ever swaps innerHTML INSIDE these three forms. So if none of them exist
     when this deferred script runs, none ever will. */
  var FORMS = document.querySelectorAll(
    '#FacetFiltersForm, #FacetFiltersFormMobile, #FacetFiltersPillsForm'
  );
  if (!FORMS.length) return;

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var summary = e.target.closest('.mobile-facets__summary');
    if (!summary) return;
    var details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS') return;
    if (!details.classList.contains('mobile-facets__details')) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    /* Dawn's drawer may have flagged the list as submenu-open — undo it */
    var main = details.closest('.mobile-facets__main');
    if (main) main.classList.remove('submenu-open');
  }, true);

  function openAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var list = scope.querySelectorAll(FACET_SELECTOR);
    for (var i = 0; i < list.length; i++) {
      var d = list[i];
      if (d.tagName === 'DETAILS' && !d.hasAttribute('open')) {
        d.setAttribute('open', '');
        var s = d.querySelector('summary');
        if (s) s.setAttribute('aria-expanded', 'true');
      }
    }
  }

  /* facets.js mostly swaps innerHTML on the EXISTING <details> (so `open`
     survives) but can also insert brand-new filter nodes, which arrive closed.
     Only those need re-opening — so filter the records instead of re-scanning
     the document, and coalesce into one rAF instead of a synchronous pass plus
     a 120ms repeat. */
  var pending = false;
  var dirty = [];

  function flush() {
    pending = false;
    var roots = dirty;
    dirty = [];
    for (var i = 0; i < roots.length; i++) openAll(roots[i]);
  }

  var mo = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (!n || n.nodeType !== 1) continue;
        if (!(n.matches && n.matches(FACET_SELECTOR)) &&
            !(n.querySelector && n.querySelector(FACET_SELECTOR))) continue;
        dirty.push(n.parentNode || n);
      }
    }
    if (!dirty.length || pending) return;
    pending = true;
    requestAnimationFrame(flush);
  });

  /* Scoped to the forms, so mutations from analytics and pixels never reach us. */
  for (var f = 0; f < FORMS.length; f++) {
    mo.observe(FORMS[f], { childList: true, subtree: true });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    if (e.target.closest('.mobile-facets__open-wrapper')) {
      requestAnimationFrame(function () { openAll(document); });
      /* the sheet can finish opening after the next frame */
      setTimeout(function () { openAll(document); }, 150);
    }
  }, true);

  openAll(document);
})();

/* ============================================================================
  QUICK VIEW — fullscreen image lightbox. Built from the quick view's own
  slides (quick-add.js strips any Liquid-rendered modal, so this is JS-only).
  Reuses the .hpx-* styling from the PDP lightbox.
  ============================================================================ */
(function () {
  if (window.__arkHqvZoom) return;
  window.__arkHqvZoom = true;

  var box = null;

  function close() {
    if (!box) return;
    box.remove();
    box = null;
    document.documentElement.style.removeProperty('overflow');
  }

  function open(col, startIndex) {
    close();
    var imgs = Array.prototype.slice.call(col.querySelectorAll('[data-hqv-slide] img'));
    if (!imgs.length) return;

    box = document.createElement('div');
    box.className = 'hqz';

    var track = document.createElement('div');
    track.className = 'hqz__track';
    imgs.forEach(function (img) {
      var fig = document.createElement('div');
      fig.className = 'hqz__slide';
      var clone = document.createElement('img');
      clone.src = img.currentSrc || img.src;
      clone.alt = img.alt || '';
      fig.appendChild(clone);
      track.appendChild(fig);
    });
    box.appendChild(track);

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'hqz__close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>';
    box.appendChild(closeBtn);

    var counter = document.createElement('div');
    counter.className = 'hqz__counter';
    box.appendChild(counter);

    var prev, next;
    if (imgs.length > 1) {
      prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'hqz__arrow hqz__arrow--prev';
      prev.setAttribute('aria-label', 'Previous image');
      prev.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>';
      next = document.createElement('button');
      next.type = 'button';
      next.className = 'hqz__arrow hqz__arrow--next';
      next.setAttribute('aria-label', 'Next image');
      next.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>';
      box.appendChild(prev);
      box.appendChild(next);
    }

    document.body.appendChild(box);
    document.documentElement.style.overflow = 'hidden';

    var slides = Array.prototype.slice.call(track.children);

    function current() {
      var c = track.scrollLeft + track.clientWidth / 2;
      var best = 0, bd = Infinity;
      slides.forEach(function (s, i) {
        var d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - c);
        if (d < bd) { bd = d; best = i; }
      });
      return best;
    }
    function goTo(i, instant) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[i].offsetLeft, behavior: instant ? 'auto' : 'smooth' });
    }
    function sync() {
      var i = current();
      counter.textContent = (i + 1) + ' / ' + slides.length;
      if (prev) prev.classList.toggle('is-disabled', i <= 0);
      if (next) next.classList.toggle('is-disabled', i >= slides.length - 1);
    }

    if (prev) prev.addEventListener('click', function () { goTo(current() - 1); });
    if (next) next.addEventListener('click', function () { goTo(current() + 1); });
    closeBtn.addEventListener('click', close);
    box.addEventListener('click', function (e) {
      if (e.target === box || e.target === track) close();
    });
    track.addEventListener('scroll', sync, { passive: true });

    document.addEventListener('keydown', function onKey(e) {
      if (!box) { document.removeEventListener('keydown', onKey); return; }
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') goTo(current() - 1);
      else if (e.key === 'ArrowRight') goTo(current() + 1);
    });

    goTo(startIndex || 0, true);
    setTimeout(sync, 30);
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var trigger = e.target.closest('[data-hqv-expand], .hqv__slide.is-active');
    if (!trigger) return;
    var col = trigger.closest('.hqv__media-col');
    if (!col) return;
    e.preventDefault();
    e.stopPropagation();
    var active = col.querySelector('[data-hqv-slide].is-active');
    var idx = active ? parseInt(active.getAttribute('data-hqv-slide'), 10) : 0;
    open(col, idx);
  });
})();
